import { createLogger } from "redux-logger";
import modelReducer, { toColumnDefinitions } from "./modelReducer";
import gridReducer from "./gridReducer";
import { errorReducer, initialErrorState } from "./errorReducer";
import { snackbarReducer, initialNotificationState } from "./snackbackReducer";
import statusCountsReducer from "./statusCountsSlice";
import { initialGridState, initialModelState } from "./initialData";
import { configureStore } from "@reduxjs/toolkit";
import { getFilterDates } from "./../components/dependencies/gridUtils";

const logger = createLogger({
  collapsed: true,
});

const initialState = {
  grid: initialGridState,
  model: initialModelState,
  error: initialErrorState,
  snackbar: initialNotificationState,
};
const cache = window.localStorage.getItem("cache");

if (cache) {
  const cacheParsed = JSON.parse(cache);
  initialState.grid = { ...cacheParsed };
}

// Ensure filterModel exists (older/partial caches may not have it).
if (!initialState.grid.filterModel) {
  initialState.grid.filterModel = {};
}

// Pre-populate column definitions from the known column state so columns exist
// in the grid before the first API response. This lets applyColumnState set row
// groups on the first render, avoiding a double load.
if (initialState.grid.columnState?.length) {
  initialState.model = {
    ...initialState.model,
    columnDefinitions: toColumnDefinitions(
      initialState.grid.columnState.map((cs: { colId: string }) => cs.colId)
    ),
  };
}

// Always reset the ValuationDate filter to the current week on startup so that
// users see today's data rather than a stale date range from a saved view.
const filterDates = getFilterDates();
initialState.grid.filterModel["ValuationDate"] = {
  dateFrom: filterDates.from,
  dateTo: filterDates.to,
  filterType: "date",
  type: "inRange",
};

const store = configureStore({
  reducer: {
    grid: gridReducer,
    model: modelReducer,
    error: errorReducer,
    snackbar: snackbarReducer,
    // Not in preloadedState: status counts are never persisted.
    statusCounts: statusCountsReducer,
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(logger),
  devTools: process.env.NODE_ENV !== "production",
  preloadedState: initialState,
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export default store;
